/**
 * ConsentGatedPixel — shared lifecycle for full-consent-gated marketing pixels (Meta, Apphud).
 *
 * Centralizes the boilerplate each pixel used to repeat verbatim: dependency validation, the
 * `initialized` flag + `deps` store, and wiring vendor init + StatsManager subscription to fire
 * once FULL consent is granted. Subclasses only supply the vendor-specific `initializePixel()`
 * and `handleStatsManagerEvent()`.
 */

import type { ConsentManager } from '../types/analytics.types';
import type { StatsManager } from '../core/stats-manager';
import type { EventName, EventData, EventMetadata } from '../types/events.types';

/** Bus-internal events that marketing pixels never forward as generic custom events. */
export const NON_FORWARDED_EVENTS = new Set<EventName>(['stats_manager_initialized', 'user_identified']);

/** Dependencies every consent-gated pixel requires. */
export interface BaseConsentDeps {
  consentManager: ConsentManager;
  statsManager: StatsManager;
}

export abstract class ConsentGatedPixel<Deps extends BaseConsentDeps> {
  protected initialized = false;
  protected deps: Partial<Deps> = {};

  /** Validate that required deps are present (throws if not) and store them. */
  protected requireDeps(deps: Partial<Deps>, requiredKeys: (keyof Deps)[], name: string): void {
    for (const key of requiredKeys) {
      if (!deps[key]) {
        throw new Error(`${name}.init() requires ${requiredKeys.map(String).join(', ')} dependencies`);
      }
    }
    this.deps = deps;
  }

  /**
   * Initialize the vendor + subscribe to StatsManager once FULL consent is granted
   * (fires immediately where consent isn't required, e.g. non-EU).
   */
  protected initOnFullConsent(): void {
    this.deps.consentManager!.onConsentGranted(() => {
      this.initializePixel();
      this.deps.statsManager!.subscribe(this.handleStatsManagerEvent.bind(this));
    });
  }

  /** Initialize the vendor SDK. Called after consent; must be idempotent via `this.initialized`. */
  protected abstract initializePixel(): void;

  /** Handle a StatsManager event (the 4-arg EventSubscriber signature). */
  protected abstract handleStatsManagerEvent(
    eventName: EventName,
    eventData: EventData,
    eventMetadata: EventMetadata,
    value: string | number | null
  ): void;
}
